import type { ProductTypeDto } from '../api/productTypes'
import { matchDeterminationLabel } from '../api/catalogImportTypes'

const normalizeSearchText = (value: string): string =>
  value.trim().toLocaleLowerCase('ru-RU').replaceAll('ё', 'е')

export type CatalogFilters = {
  productTypeL0Id: number | null
  productTypeL0Query: string
  productTypeL1Id: number | null
  productTypeL1Query: string
  productTypeL2Id: number | null
  productTypeL2Query: string
  country: string
  brand: string
  /** Select key: `org:{id}` | `private:{id}` | empty */
  supplierKey: string
  supplierLabel: string
  minPrice: string
  maxPrice: string
  nameQuery: string
  myPricesOnly: boolean
  matchStatus: string
  unassignedOnly: boolean
  page: number
}

export type CatalogSupplierRef = { kind: 'org' | 'private'; id: number }

export const catalogSupplierKey = (kind: 'org' | 'private', id: number): string => `${kind}:${id}`

export const parseCatalogSupplierKey = (key: string): CatalogSupplierRef | null => {
  const trimmed = key.trim()
  if (trimmed.startsWith('org:')) {
    const id = Number(trimmed.slice(4))
    return Number.isFinite(id) && id > 0 ? { kind: 'org', id } : null
  }
  if (trimmed.startsWith('private:')) {
    const id = Number(trimmed.slice(8))
    return Number.isFinite(id) && id > 0 ? { kind: 'private', id } : null
  }
  return null
}

export const emptyCatalogFilters = (): CatalogFilters => defaultCatalogFilters(false)

export const defaultCatalogFilters = (myPricesOnly: boolean): CatalogFilters => ({
  productTypeL0Id: null,
  productTypeL0Query: '',
  productTypeL1Id: null,
  productTypeL1Query: '',
  productTypeL2Id: null,
  productTypeL2Query: '',
  country: '',
  brand: '',
  supplierKey: '',
  supplierLabel: '',
  minPrice: '',
  maxPrice: '',
  nameQuery: '',
  myPricesOnly,
  matchStatus: '',
  unassignedOnly: false,
  page: 0,
})

export const matchesCatalogTextFilter = (value: string, query: string): boolean => {
  const normalized = normalizeSearchText(query)
  if (!normalized) {
    return true
  }
  return normalizeSearchText(value).includes(normalized)
}

export const nameQueryTags = (value: string): string[] => {
  const normalized = normalizeSearchText(value)
  return normalized ? [normalized] : []
}

export const formatProductTypeLabel = (
  productTypeId: number | null,
  productTypeName: string | null | undefined,
  productTypeActive: boolean | null | undefined,
  typeNames: Map<number, string>,
): string => {
  if (productTypeId == null) {
    return '—'
  }
  const name = productTypeName ?? typeNames.get(productTypeId)
  if (!name) {
    return `#${productTypeId}`
  }
  if (productTypeActive === false) {
    return `${name} (выкл.)`
  }
  return name
}

export const isL0ProductType = (type: ProductTypeDto): boolean =>
  type.parentId == null && type.active !== false && Boolean(type.code?.trim())

export const isL1ProductType = (type: ProductTypeDto): boolean => productTypeLevel(type) === 1

export const isL2ProductType = (type: ProductTypeDto): boolean => productTypeLevel(type) === 2

export const isL3ProductType = (type: ProductTypeDto): boolean => productTypeLevel(type) === 3

export const productTypeLevel = (type: ProductTypeDto): number => {
  if (!type.code?.trim()) {
    return -1
  }
  return type.code.split('.').length - 1
}

export const listChildProductTypes = (
  parent: ProductTypeDto,
  productTypes: ProductTypeDto[],
  childLevel: number,
): ProductTypeDto[] =>
  productTypes.filter(
    (type) => productTypeLevel(type) === childLevel && type.code.startsWith(`${parent.code}.`),
  )

export const getEffectiveProductTypeId = (filters: CatalogFilters): number | null =>
  filters.productTypeL2Id ?? filters.productTypeL1Id ?? filters.productTypeL0Id ?? null

export type ProductTypeCascadeSelection = {
  l0Id: number | null
  l1Id: number | null
  l2Id: number | null
  l3Id: number | null
}

export const productTypeAncestorAtLevel = (
  type: ProductTypeDto,
  productTypes: ProductTypeDto[],
  targetLevel: number,
): ProductTypeDto | null => {
  if (!type.code) {
    return productTypeLevel(type) === targetLevel ? type : null
  }
  const segments = type.code.split('.')
  if (targetLevel >= segments.length) {
    return null
  }
  const ancestorCode = segments.slice(0, targetLevel + 1).join('.')
  return productTypes.find((item) => item.code === ancestorCode) ?? null
}

export const resolveProductTypeCascade = (
  productTypeId: number | null,
  productTypes: ProductTypeDto[],
): ProductTypeCascadeSelection => {
  if (productTypeId == null) {
    return { l0Id: null, l1Id: null, l2Id: null, l3Id: null }
  }
  const selected = productTypes.find((type) => type.id === productTypeId) ?? null
  if (!selected) {
    return { l0Id: null, l1Id: null, l2Id: null, l3Id: null }
  }
  const level = productTypeLevel(selected)
  const l0 = productTypeAncestorAtLevel(selected, productTypes, 0)
  const l1 = level >= 1 ? productTypeAncestorAtLevel(selected, productTypes, 1) : null
  const l2 = level >= 2 ? productTypeAncestorAtLevel(selected, productTypes, 2) : null
  return {
    l0Id: l0?.id ?? (level === 0 ? selected.id : null),
    l1Id: l1?.id ?? (level === 1 ? selected.id : null),
    l2Id: l2?.id ?? (level === 2 ? selected.id : null),
    l3Id: level >= 3 ? selected.id : null,
  }
}

/** @deprecated Prefer L2+L3 cascade: listChildProductTypes(parent, types, 2|3). */
export const listAssignableProductTypesUnderL1 = (
  parentL1: ProductTypeDto,
  productTypes: ProductTypeDto[],
): ProductTypeDto[] =>
  productTypes
    .filter((type) => {
      if (type.id === parentL1.id && productTypeLevel(type) >= 2) {
        return true
      }
      return type.code.startsWith(`${parentL1.code}.`) && productTypeLevel(type) >= 2
    })
    .sort((left, right) => left.name.localeCompare(right.name, 'ru'))

export const effectiveProductTypeIdFromCascade = (
  cascade: ProductTypeCascadeSelection,
): number | null => cascade.l3Id ?? cascade.l2Id ?? cascade.l1Id ?? cascade.l0Id ?? null

export const expandProductTypeToDescendantIds = (
  productTypeId: number,
  productTypes: ProductTypeDto[],
): number[] => {
  const selected = productTypes.find((type) => type.id === productTypeId)
  if (!selected) {
    return [productTypeId]
  }
  const byParent = new Map<number, ProductTypeDto[]>()
  for (const type of productTypes) {
    if (type.parentId != null) {
      const siblings = byParent.get(type.parentId) ?? []
      siblings.push(type)
      byParent.set(type.parentId, siblings)
    }
  }
  const ids = new Set<number>([selected.id])
  const prefix = `${selected.code}.`
  for (const type of productTypes) {
    if (type.id === selected.id || type.code.startsWith(prefix)) {
      ids.add(type.id)
    }
  }
  const stack = [selected.id]
  while (stack.length > 0) {
    const parentId = stack.pop()!
    for (const child of byParent.get(parentId) ?? []) {
      if (ids.has(child.id)) {
        continue
      }
      ids.add(child.id)
      stack.push(child.id)
    }
  }
  return [...ids]
}

export const filterSuggestions = (options: string[], query: string, limit = 80): string[] => {
  const normalized = normalizeSearchText(query)
  if (!normalized) {
    return options.slice(0, limit)
  }
  return options
    .filter((option) => normalizeSearchText(option).includes(normalized))
    .slice(0, limit)
}

export const findProductTypeL0ByName = (
  types: Array<{ id: number; name: string }>,
  query: string,
): { id: number; name: string } | null => {
  const normalized = normalizeSearchText(query)
  if (!normalized) {
    return null
  }
  return types.find((type) => normalizeSearchText(type.name) === normalized) ?? null
}

export const findProductTypeByName = (
  types: ProductTypeDto[],
  query: string,
): ProductTypeDto | null => {
  const normalized = normalizeSearchText(query)
  if (!normalized) {
    return null
  }
  return types.find((type) => normalizeSearchText(type.name) === normalized) ?? null
}

export type CatalogFilterChip = {
  id: string
  label: string
}

const priceChipLabel = (minPrice: string, maxPrice: string): string | null => {
  const min = minPrice.trim()
  const max = maxPrice.trim()
  if (min && max) {
    return `Цена ${min}–${max}`
  }
  if (min) {
    return `Цена от ${min}`
  }
  if (max) {
    return `Цена до ${max}`
  }
  return null
}

export const buildCatalogFilterChips = (
  filters: CatalogFilters,
  defaults: CatalogFilters,
): CatalogFilterChip[] => {
  const chips: CatalogFilterChip[] = []

  if (filters.nameQuery.trim()) {
    chips.push({ id: 'nameQuery', label: `Название: ${filters.nameQuery.trim()}` })
  }
  if (filters.productTypeL0Query.trim()) {
    chips.push({ id: 'productTypeL0', label: `Категория: ${filters.productTypeL0Query.trim()}` })
  }
  if (filters.productTypeL1Query.trim()) {
    chips.push({ id: 'productTypeL1', label: `Категория: ${filters.productTypeL1Query.trim()}` })
  }
  if (filters.productTypeL2Query.trim()) {
    chips.push({ id: 'productTypeL2', label: `Подкатегория: ${filters.productTypeL2Query.trim()}` })
  }
  const priceLabel = priceChipLabel(filters.minPrice, filters.maxPrice)
  if (priceLabel) {
    chips.push({ id: 'price', label: priceLabel })
  }
  if (filters.country.trim()) {
    chips.push({ id: 'country', label: `Страна: ${filters.country.trim()}` })
  }
  if (filters.brand.trim()) {
    chips.push({ id: 'brand', label: `Бренд: ${filters.brand.trim()}` })
  }
  if (filters.supplierKey.trim()) {
    chips.push({
      id: 'supplier',
      label: `Поставщик: ${filters.supplierLabel.trim() || filters.supplierKey.trim()}`,
    })
  }
  if (filters.matchStatus.trim()) {
    chips.push({
      id: 'matchStatus',
      label: `Тип определения: ${matchDeterminationLabel(filters.matchStatus.trim())}`,
    })
  }
  if (filters.unassignedOnly) {
    chips.push({ id: 'unassignedOnly', label: 'Только без категории' })
  }
  if (filters.myPricesOnly !== defaults.myPricesOnly && filters.myPricesOnly) {
    chips.push({ id: 'myPricesOnly', label: 'Только мои прайсы' })
  }

  return chips
}

export const clearCatalogFilterChip = (
  filters: CatalogFilters,
  chipId: string,
  defaults: CatalogFilters,
): CatalogFilters => {
  switch (chipId) {
    case 'nameQuery':
      return { ...filters, nameQuery: '' }
    case 'productTypeL0':
      return {
        ...filters,
        productTypeL0Id: null,
        productTypeL0Query: '',
        productTypeL1Id: null,
        productTypeL1Query: '',
        productTypeL2Id: null,
        productTypeL2Query: '',
      }
    case 'productTypeL1':
      return {
        ...filters,
        productTypeL1Id: null,
        productTypeL1Query: '',
        productTypeL2Id: null,
        productTypeL2Query: '',
      }
    case 'productTypeL2':
      return { ...filters, productTypeL2Id: null, productTypeL2Query: '' }
    case 'price':
      return { ...filters, minPrice: '', maxPrice: '' }
    case 'country':
      return { ...filters, country: '' }
    case 'brand':
      return { ...filters, brand: '' }
    case 'supplier':
      return { ...filters, supplierKey: '', supplierLabel: '' }
    case 'matchStatus':
      return { ...filters, matchStatus: '' }
    case 'unassignedOnly':
      return { ...filters, unassignedOnly: false }
    case 'myPricesOnly':
      return { ...filters, myPricesOnly: defaults.myPricesOnly }
    default:
      return filters
  }
}
